/**
 * Settings → Integrations — manage external service connections.
 *
 * Shows configured integrations (Harvest, Slack, Resend) with
 * status, config, and toggle. Real API keys live in Workers Secrets;
 * this manages the user-facing configuration layer.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, ExternalLink } from 'lucide-react';

import {
  useIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
} from '@/hooks/use-integrations.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

const INTEGRATION_KINDS = [
  {
    id: 'harvest' as const,
    label: 'Harvest',
    description: 'Time tracking sync — pulls entries, pushes manual logs',
    configFields: [
      { key: 'accountId', label: 'Account ID', type: 'text' },
      { key: 'projectId', label: 'Default Project ID', type: 'text' },
    ],
  },
  {
    id: 'slack' as const,
    label: 'Slack',
    description: 'Ticket notifications, breach alerts, assignment mentions',
    configFields: [
      { key: 'webhookUrl', label: 'Webhook URL', type: 'url' },
      { key: 'defaultChannel', label: 'Default Channel', type: 'text' },
    ],
  },
  {
    id: 'resend' as const,
    label: 'Resend',
    description: 'Outbound email — client replies, reports, magic links',
    configFields: [
      { key: 'fromDomain', label: 'Sending Domain', type: 'text' },
      { key: 'fromName', label: 'Sender Name', type: 'text' },
    ],
  },
];

export function SettingsIntegrations() {
  const query = useIntegrations();
  const create = useCreateIntegration();
  const update = useUpdateIntegration();
  const remove = useDeleteIntegration();
  const [adding, setAdding] = useState<string | null>(null);

  const integrations = query.data ?? [];

  function handleToggle(id: string, isActive: boolean) {
    update.mutate(
      { id, isActive: !isActive },
      {
        onSuccess: () =>
          toast.success(isActive ? 'Integration paused' : 'Integration enabled'),
      }
    );
  }

  function handleDelete(id: string) {
    remove.mutate(id, {
      onSuccess: () => toast.success('Integration removed'),
    });
  }

  function handleAdd(kind: 'harvest' | 'slack' | 'resend') {
    const def = INTEGRATION_KINDS.find((k) => k.id === kind)!;
    create.mutate(
      { kind, label: def.label, configJson: {} },
      {
        onSuccess: () => {
          toast.success(`${def.label} integration added`);
          setAdding(null);
        },
      }
    );
  }

  if (query.isLoading) {
    return (
      <div className="p-6 space-y-4 max-w-2xl animate-pulse">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-ink-700 rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-ink-100">
            Integrations
          </h2>
          <p className="text-[13px] text-ink-300 mt-0.5">
            External services connected to Beacon. API keys are managed via
            environment secrets — this page configures the connection settings.
          </p>
        </div>
      </div>

      {/* Configured integrations */}
      <div className="space-y-3">
        {integrations.map((integ) => {
          const def = INTEGRATION_KINDS.find((k) => k.id === integ.kind);
          return (
            <div
              key={integ.id}
              className="rounded-md border border-ink-600 p-4 bg-ink-800"
              data-testid={`integ-${integ.kind}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-medium text-ink-100">
                    {integ.label}
                  </span>
                  <Badge tone={integ.isActive ? 'green' : 'slate'}>
                    {integ.isActive ? 'Active' : 'Paused'}
                  </Badge>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleToggle(integ.id, integ.isActive)}
                    className="h-7 px-2.5 rounded text-[12px] bg-ink-700 border border-ink-600 text-ink-200 hover:text-ink-100 focus-ring"
                  >
                    {integ.isActive ? 'Pause' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleDelete(integ.id)}
                    className="h-7 px-2 rounded text-ink-400 hover:text-danger-400 bg-ink-700 border border-ink-600 focus-ring"
                    aria-label="Remove integration"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {def && (
                <p className="text-[12.5px] text-ink-300">
                  {def.description}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Add integration */}
      {integrations.length < 3 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.06em] text-ink-400 mb-2">
            Available integrations
          </p>
          <div className="space-y-2">
            {INTEGRATION_KINDS.filter(
              (k) => !integrations.some((i) => i.kind === k.id)
            ).map((kind) => (
              <button
                key={kind.id}
                onClick={() => handleAdd(kind.id)}
                disabled={create.isPending}
                data-testid={`integ-add-${kind.id}`}
                className="w-full flex items-center justify-between rounded-md border border-ink-600 border-dashed p-3 text-left hover:border-ink-500 hover:bg-ink-800/50 focus-ring transition-colors"
              >
                <div>
                  <span className="text-[13px] text-ink-200 font-medium">
                    {kind.label}
                  </span>
                  <p className="text-[12px] text-ink-400 mt-0.5">
                    {kind.description}
                  </p>
                </div>
                <Plus size={16} className="text-ink-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-ink-600">
        <p className="text-[12px] text-ink-400">
          API keys (RESEND_API_KEY, HARVEST_TOKEN, etc.) are stored as{' '}
          <span className="mono">Workers Secrets</span> and managed via
          wrangler or the Cloudflare dashboard — not here.
        </p>
      </div>
    </div>
  );
}
